import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { LOADING_STATUS } from '../../common/constants';
import {
  createDac7Report,
  CreateDac7ReportRequest,
  Dac7ReportResponse,
  generateDac7Xml,
  GenerateDac7XmlRequest,
  GenerateDac7XmlResponse,
  getDac7Reports,
  getWinTimeImports,
  importWinTimeData,
  MarkAsSubmittedRequest,
  markDac7ReportAsSubmitted,
  ProcessingStartedResponse,
  processWinTimeData,
  ProcessWinTimeDataResponse,
  WinTimeImportResponse,
} from '../../common/api/dac7Service';
import { RootState } from '../../app/store';
import {
  Dac7CompletionUpdate,
  Dac7ProgressUpdate,
} from '../../common/hubs/signalRDac7Service';

export interface Dac7State {
  reports: Dac7ReportResponse[];
  selectedReport: Dac7ReportResponse | null;
  winTimeImports: WinTimeImportResponse[];
  loadingStatus: LOADING_STATUS;
  createStatus: LOADING_STATUS;
  importStatus: LOADING_STATUS;
  processStatus: LOADING_STATUS;
  winTimeImportsStatus: LOADING_STATUS;
  generateXmlStatus: LOADING_STATUS;
  markAsSubmittedStatus: LOADING_STATUS;
  lastProcessResult: ProcessWinTimeDataResponse | null;
  lastXmlResult: GenerateDac7XmlResponse | null;
  error: string | null;
  // SignalR progress state
  processingProgress: Dac7ProgressUpdate | null;
  processingComplete: Dac7CompletionUpdate | null;
  // Background processing state
  isProcessingInBackground: boolean;
}

const initialState: Dac7State = {
  reports: [],
  selectedReport: null,
  winTimeImports: [],
  loadingStatus: LOADING_STATUS.IDLE,
  createStatus: LOADING_STATUS.IDLE,
  importStatus: LOADING_STATUS.IDLE,
  processStatus: LOADING_STATUS.IDLE,
  winTimeImportsStatus: LOADING_STATUS.IDLE,
  generateXmlStatus: LOADING_STATUS.IDLE,
  markAsSubmittedStatus: LOADING_STATUS.IDLE,
  lastProcessResult: null,
  lastXmlResult: null,
  error: null,
  // SignalR progress state
  processingProgress: null,
  processingComplete: null,
  // Background processing state
  isProcessingInBackground: false,
};

export const fetchDac7ReportsAsync = createAsyncThunk(
  'dac7/fetchReports',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getDac7Reports();
      if (response.isSuccess) {
        return response.result || [];
      }
      return rejectWithValue(response.message || 'Failed to fetch reports');
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to fetch reports';
      return rejectWithValue(errorMessage);
    }
  },
);

export const createDac7ReportAsync = createAsyncThunk(
  'dac7/createReport',
  async (request: CreateDac7ReportRequest, { rejectWithValue }) => {
    try {
      const response = await createDac7Report(request);
      if (response.isSuccess) {
        return response.result;
      }
      return rejectWithValue(response.message || 'Failed to create report');
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to create report';
      return rejectWithValue(errorMessage);
    }
  },
);

export const importWinTimeDataAsync = createAsyncThunk(
  'dac7/importWinTimeData',
  async (
    { reportId, file }: { reportId: string; file: File },
    { rejectWithValue },
  ) => {
    try {
      const response = await importWinTimeData(reportId, file);
      if (response.isSuccess) {
        return response.result;
      }
      return rejectWithValue(
        response.message || 'Failed to import WinTime data',
      );
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to import WinTime data';
      return rejectWithValue(errorMessage);
    }
  },
);

// This now starts background processing - actual results come via SignalR
export const processWinTimeDataAsync = createAsyncThunk(
  'dac7/processWinTimeData',
  async (reportId: string, { rejectWithValue }) => {
    try {
      const response: ProcessingStartedResponse =
        await processWinTimeData(reportId);
      // Background processing started successfully
      return response;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to start WinTime data processing';
      return rejectWithValue(errorMessage);
    }
  },
);

export const fetchWinTimeImportsAsync = createAsyncThunk(
  'dac7/fetchWinTimeImports',
  async (reportId: string, { rejectWithValue }) => {
    try {
      const response = await getWinTimeImports(reportId);
      if (response.isSuccess) {
        return response.result || [];
      }
      return rejectWithValue(
        response.message || 'Failed to fetch WinTime imports',
      );
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to fetch WinTime imports';
      return rejectWithValue(errorMessage);
    }
  },
);

export const generateDac7XmlAsync = createAsyncThunk(
  'dac7/generateXml',
  async (
    {
      reportId,
      request,
    }: { reportId: string; request?: GenerateDac7XmlRequest },
    { rejectWithValue },
  ) => {
    try {
      const response = await generateDac7Xml(reportId, request);
      if (response.isSuccess && response.result) {
        return response.result;
      }
      return rejectWithValue(response.message || 'Failed to generate XML');
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to generate XML';
      return rejectWithValue(errorMessage);
    }
  },
);

export const markDac7ReportAsSubmittedAsync = createAsyncThunk(
  'dac7/markAsSubmitted',
  async (
    {
      reportId,
      request,
    }: { reportId: string; request?: MarkAsSubmittedRequest },
    { rejectWithValue },
  ) => {
    try {
      const response = await markDac7ReportAsSubmitted(reportId, request);
      if (response.isSuccess && response.result) {
        return response.result;
      }
      return rejectWithValue(
        response.message || 'Failed to mark report as submitted',
      );
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to mark report as submitted';
      return rejectWithValue(errorMessage);
    }
  },
);

const dac7Slice = createSlice({
  name: 'dac7',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
    setSelectedReport: (
      state,
      action: PayloadAction<Dac7ReportResponse | null>,
    ) => {
      state.selectedReport = action.payload;
    },
    resetCreateStatus: state => {
      state.createStatus = LOADING_STATUS.IDLE;
    },
    resetImportStatus: state => {
      state.importStatus = LOADING_STATUS.IDLE;
    },
    resetProcessStatus: state => {
      state.processStatus = LOADING_STATUS.IDLE;
      state.isProcessingInBackground = false;
    },
    resetGenerateXmlStatus: state => {
      state.generateXmlStatus = LOADING_STATUS.IDLE;
      state.lastXmlResult = null;
    },
    resetMarkAsSubmittedStatus: state => {
      state.markAsSubmittedStatus = LOADING_STATUS.IDLE;
    },
    addWinTimeImport: (state, action: PayloadAction<WinTimeImportResponse>) => {
      state.winTimeImports.unshift(action.payload);
    },
    // SignalR progress actions
    setProcessingProgress: (
      state,
      action: PayloadAction<Dac7ProgressUpdate>,
    ) => {
      state.processingProgress = action.payload;
    },
    setProcessingComplete: (
      state,
      action: PayloadAction<Dac7CompletionUpdate>,
    ) => {
      state.processingComplete = action.payload;
      state.isProcessingInBackground = false;
      state.processStatus = action.payload.success
        ? LOADING_STATUS.IDLE
        : LOADING_STATUS.FAILED;
      if (!action.payload.success) {
        state.error = action.payload.message || 'Processing failed';
      }
    },
    clearProcessingProgress: state => {
      state.processingProgress = null;
      state.processingComplete = null;
    },
  },
  extraReducers: builder => {
    builder
      // Fetch reports
      .addCase(fetchDac7ReportsAsync.pending, state => {
        state.loadingStatus = LOADING_STATUS.LOADING;
        state.error = null;
      })
      .addCase(fetchDac7ReportsAsync.fulfilled, (state, action) => {
        state.loadingStatus = LOADING_STATUS.IDLE;
        state.reports = action.payload;
      })
      .addCase(fetchDac7ReportsAsync.rejected, (state, action) => {
        state.loadingStatus = LOADING_STATUS.FAILED;
        state.error = action.payload as string;
      })
      // Create report
      .addCase(createDac7ReportAsync.pending, state => {
        state.createStatus = LOADING_STATUS.LOADING;
        state.error = null;
      })
      .addCase(createDac7ReportAsync.fulfilled, (state, action) => {
        state.createStatus = LOADING_STATUS.IDLE;
        if (action.payload) {
          state.reports.unshift(action.payload);
          state.selectedReport = action.payload;
        }
      })
      .addCase(createDac7ReportAsync.rejected, (state, action) => {
        state.createStatus = LOADING_STATUS.FAILED;
        state.error = action.payload as string;
      })
      // Import WinTime data
      .addCase(importWinTimeDataAsync.pending, state => {
        state.importStatus = LOADING_STATUS.LOADING;
        state.error = null;
      })
      .addCase(importWinTimeDataAsync.fulfilled, (state, action) => {
        state.importStatus = LOADING_STATUS.IDLE;
        if (action.payload) {
          state.winTimeImports.unshift(action.payload);
        }
      })
      .addCase(importWinTimeDataAsync.rejected, (state, action) => {
        state.importStatus = LOADING_STATUS.FAILED;
        state.error = action.payload as string;
      })
      // Process WinTime data - now starts background processing
      .addCase(processWinTimeDataAsync.pending, state => {
        state.processStatus = LOADING_STATUS.LOADING;
        state.error = null;
        state.processingProgress = null;
        state.processingComplete = null;
      })
      .addCase(processWinTimeDataAsync.fulfilled, state => {
        // Processing started in background - keep LOADING status
        // Actual completion will be handled by SignalR via setProcessingComplete
        state.isProcessingInBackground = true;
        // Don't set to IDLE here - wait for SignalR completion
      })
      .addCase(processWinTimeDataAsync.rejected, (state, action) => {
        state.processStatus = LOADING_STATUS.FAILED;
        state.isProcessingInBackground = false;
        state.error = action.payload as string;
      })
      // Fetch WinTime imports
      .addCase(fetchWinTimeImportsAsync.pending, state => {
        state.winTimeImportsStatus = LOADING_STATUS.LOADING;
        state.error = null;
      })
      .addCase(fetchWinTimeImportsAsync.fulfilled, (state, action) => {
        state.winTimeImportsStatus = LOADING_STATUS.IDLE;
        state.winTimeImports = action.payload;
      })
      .addCase(fetchWinTimeImportsAsync.rejected, (state, action) => {
        state.winTimeImportsStatus = LOADING_STATUS.FAILED;
        state.error = action.payload as string;
      })
      // Generate XML
      .addCase(generateDac7XmlAsync.pending, state => {
        state.generateXmlStatus = LOADING_STATUS.LOADING;
        state.error = null;
        state.lastXmlResult = null;
      })
      .addCase(generateDac7XmlAsync.fulfilled, (state, action) => {
        state.generateXmlStatus = LOADING_STATUS.IDLE;
        state.lastXmlResult = action.payload;
        // Update the report status in the list
        const reportIndex = state.reports.findIndex(
          r => r.id === action.payload.reportId,
        );
        if (reportIndex !== -1) {
          state.reports[reportIndex].status = 'Generated';
        }
        if (state.selectedReport?.id === action.payload.reportId) {
          state.selectedReport.status = 'Generated';
        }
      })
      .addCase(generateDac7XmlAsync.rejected, (state, action) => {
        state.generateXmlStatus = LOADING_STATUS.FAILED;
        state.error = action.payload as string;
      })
      // Mark as submitted
      .addCase(markDac7ReportAsSubmittedAsync.pending, state => {
        state.markAsSubmittedStatus = LOADING_STATUS.LOADING;
        state.error = null;
      })
      .addCase(markDac7ReportAsSubmittedAsync.fulfilled, (state, action) => {
        state.markAsSubmittedStatus = LOADING_STATUS.IDLE;
        // Update the report status in the list
        const reportIndex = state.reports.findIndex(
          r => r.id === action.payload.id,
        );
        if (reportIndex !== -1) {
          state.reports[reportIndex] = action.payload;
        }
        if (state.selectedReport?.id === action.payload.id) {
          state.selectedReport = action.payload;
        }
      })
      .addCase(markDac7ReportAsSubmittedAsync.rejected, (state, action) => {
        state.markAsSubmittedStatus = LOADING_STATUS.FAILED;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  setSelectedReport,
  resetCreateStatus,
  resetImportStatus,
  resetProcessStatus,
  resetGenerateXmlStatus,
  resetMarkAsSubmittedStatus,
  addWinTimeImport,
  setProcessingProgress,
  setProcessingComplete,
  clearProcessingProgress,
} = dac7Slice.actions;

// Selectors
export const selectDac7Reports = (state: RootState) => state.dac7.reports;
export const selectDac7SelectedReport = (state: RootState) =>
  state.dac7.selectedReport;
export const selectDac7WinTimeImports = (state: RootState) =>
  state.dac7.winTimeImports;
export const selectDac7LoadingStatus = (state: RootState) =>
  state.dac7.loadingStatus;
export const selectDac7CreateStatus = (state: RootState) =>
  state.dac7.createStatus;
export const selectDac7ImportStatus = (state: RootState) =>
  state.dac7.importStatus;
export const selectDac7ProcessStatus = (state: RootState) =>
  state.dac7.processStatus;
export const selectDac7WinTimeImportsStatus = (state: RootState) =>
  state.dac7.winTimeImportsStatus;
export const selectDac7GenerateXmlStatus = (state: RootState) =>
  state.dac7.generateXmlStatus;
export const selectDac7LastProcessResult = (state: RootState) =>
  state.dac7.lastProcessResult;
export const selectDac7LastXmlResult = (state: RootState) =>
  state.dac7.lastXmlResult;
export const selectDac7Error = (state: RootState) => state.dac7.error;
export const selectDac7ProcessingProgress = (state: RootState) =>
  state.dac7.processingProgress;
export const selectDac7ProcessingComplete = (state: RootState) =>
  state.dac7.processingComplete;
export const selectDac7IsProcessingInBackground = (state: RootState) =>
  state.dac7.isProcessingInBackground;
export const selectDac7MarkAsSubmittedStatus = (state: RootState) =>
  state.dac7.markAsSubmittedStatus;

export default dac7Slice.reducer;
